import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  ORGANISATION_ADMIN_ROLE,
  COORDINATOR_ROLE,
  CARER_ROLE,
  FAMILY_ROLE,
  CUSTOMER_MEMBER_ROLE,
  CUSTOMER_FAMILY_REPRESENTATIVE_ROLE,
  TENANT_ADMINISTRATOR,
  CUSTOMER_FAMILY_ROLE,
  CUSTOMER_SELF_ROLE,
  CUSTOMER_COORDINATOR_ROLE,
} from '@careapp-group/core';

const ROLE_ORDER = {
  [CUSTOMER_SELF_ROLE]: 0,
  [CUSTOMER_MEMBER_ROLE]: 1,
  [CUSTOMER_FAMILY_REPRESENTATIVE_ROLE]: 2,
  [CUSTOMER_FAMILY_ROLE]: 3,
  [FAMILY_ROLE]: 4,
  [CARER_ROLE]: 5,
  [ORGANISATION_ADMIN_ROLE]: 6,
  [TENANT_ADMINISTRATOR]: 7,
  [COORDINATOR_ROLE]: 8,
  [CUSTOMER_COORDINATOR_ROLE]: 9,
} as const;

type RoleType = keyof typeof ROLE_ORDER;

function isKnownRole(role: string): role is RoleType {
  return role in ROLE_ORDER;
}

/**
 * The data to be passed to the SelectRoleDialog through MAT_DIALOG_DATA
 */
export interface SelectRoleDialogData {
  /**
   * The current selection, if any
   */
  selectedRoles: string[];

  /**
   * The roles that should be displayed, but be disabled
   */
  disabledRoles?: string[];

  /**
   * The roles that should be displayed. If missing, defaults to:
   *  - Org Admin
   *  - Coordinator
   *  - Carer
   *  - Family
   *  - Customer Member
   */
  roleOptions?: string[];

  // If passing in roles unknown to the role order, determine their list placement.
  additionalRolePosition?: 'end' | 'start';
}

@Component({
    selector: 'select-role-dialog',
    templateUrl: 'select-role-dialog.component.html',
    styleUrls: ['select-role-dialog.component.scss'],
    standalone: false
})
export class SelectRoleDialogComponent implements OnInit {
  public form: UntypedFormGroup;
  userRoles: string[] = [];
  private additionalRolePosition: 'end' | 'start';

  constructor(
    private dialogRef: MatDialogRef<any>,
    @Inject(MAT_DIALOG_DATA)
    public data: SelectRoleDialogData
  ) {
    this.additionalRolePosition = data.additionalRolePosition || 'end';

    // Use the role constants for default roles
    const roles = data.roleOptions || [
      CUSTOMER_MEMBER_ROLE,
      CUSTOMER_FAMILY_REPRESENTATIVE_ROLE,
      FAMILY_ROLE,
      CARER_ROLE,
      ORGANISATION_ADMIN_ROLE,
      COORDINATOR_ROLE,
    ];

    this.userRoles = this.sortRoles(roles);
  }

  private sortRoles(roles: string[]): string[] {
    const knownRoles: string[] = [];
    const unknownRoles: string[] = [];

    roles.forEach((role) => {
      if (isKnownRole(role)) {
        knownRoles.push(role);
      } else {
        unknownRoles.push(role);
      }
    });

    // Sort roles
    const sortedKnownRoles = knownRoles.sort((a, b) => {
      return ROLE_ORDER[a as RoleType] - ROLE_ORDER[b as RoleType];
    });

    // Put unknown roles at the end, unless told otherwise.
    switch (this.additionalRolePosition) {
      case 'start':
        return [...unknownRoles, ...sortedKnownRoles];
      case 'end':
      default:
        return [...sortedKnownRoles, ...unknownRoles];
    }
  }

  ngOnInit() {
    this.form = new UntypedFormGroup({
      roles: new UntypedFormControl(this.data.selectedRoles ?? []),
      disabledRoles: new UntypedFormControl(this.data.disabledRoles ?? []),
    });
  }

  submit() {
    this.dialogRef.close(this.form.controls.roles.value);
  }

  updateQuestionRoles(role: string, checked: boolean) {
    let formRolesArray: string[] = this.form.get('roles').value ?? [];
    this.form.get('roles').patchValue(this.updateRolesInArray(formRolesArray, role, checked));
  }

  // Depending on 'isAdding', will add if !exists, or remove if exists.
  updateRolesInArray(array: string[], role: string, isAdding: boolean): string[] {
    if (isAdding && array.indexOf(role) === -1) {
      return [...array, role];
      // Add this role if it does not exist already.
    } else if (!isAdding && array.indexOf(role) !== -1) {
      // Remove this role, if it exists.
      return [...array].filter((r) => r !== role);
    }
  }

  toggleSelectAll(checked: boolean) {
    if (checked) {
      this.form.get('roles').patchValue(this.userRoles);
    } else {
      this.form
        .get('roles')
        .patchValue(this.data.selectedRoles ?? [ORGANISATION_ADMIN_ROLE, COORDINATOR_ROLE]);
    }
  }
}
